import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ArrowUpRight, Check, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import ScrollReveal from "../ScrollReveal";
import { SERVICES, CALENDLY_URL, MOST_POPULAR_SLUG } from "../../data/content";

const Card = ({ s }) => {
  const popular = s.slug === MOST_POPULAR_SLUG;
  return (
    <div
      data-testid={`pricing-card-${s.slug}`}
      className={`group relative h-full flex flex-col p-8 md:p-10 rounded-3xl transition-all duration-500 ${
        popular
          ? "bg-gradient-to-br from-[#1a0a2e] via-[#0d0418] to-[#0a0314] neon-border-strong neon-pulse"
          : "bg-[#0a0a0a] border border-white/10 hover:border-violet-400/40"
      }`}
    >
      {popular && (
        <>
          {/* Neon accent corners */}
          <div className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-32 h-px bg-gradient-to-r from-transparent via-violet-400 to-transparent" />
          <div className="pointer-events-none absolute bottom-0 left-1/2 -translate-x-1/2 w-32 h-px bg-gradient-to-r from-transparent via-violet-400 to-transparent" />
        </>
      )}

      {popular && (
        <div
          data-testid="pricing-popular-badge"
          className="absolute -top-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 px-4 py-2 rounded-full text-[10px] uppercase tracking-[0.3em] font-bold bg-black border border-violet-400/70 whitespace-nowrap z-10"
          style={{
            boxShadow: "0 0 25px rgba(139, 92, 246, 0.7), 0 0 50px rgba(139, 92, 246, 0.35)",
            color: "#E9D5FF",
            textShadow: "0 0 10px rgba(196, 181, 253, 0.8)",
          }}
        >
          <Sparkles size={12} className="text-violet-300" />
          Most Popular
        </div>
      )}

      <div className={`text-xs uppercase tracking-[0.3em] mb-3 ${popular ? "neon-text" : "text-white/40"}`}>
        {s.title}
      </div>
      <div className="flex items-baseline gap-2 mb-5">
        <div
          className={`font-display text-4xl md:text-5xl font-black tracking-tighter ${
            popular ? "neon-text" : "text-white"
          }`}
        >
          {s.price}
        </div>
      </div>
      <p className="text-sm text-white/60 leading-relaxed mb-8">{s.summary}</p>

      <ul className="space-y-3 mb-10 flex-1">
        {s.deliverables.map((d) => (
          <li key={d} className="flex items-start gap-3 text-sm text-white/80">
            <div
              className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                popular ? "bg-violet-500/30 border border-violet-400/60" : "bg-white/5 border border-white/10"
              }`}
            >
              <Check size={11} className={popular ? "text-violet-200" : "text-white"} strokeWidth={2.5} />
            </div>
            <span>{d}</span>
          </li>
        ))}
      </ul>

      <a
        href={CALENDLY_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`pricing-cta-${s.slug}`}
        className={`inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full text-xs uppercase tracking-[0.25em] font-semibold transition-all ${
          popular
            ? "btn-violet"
            : "border border-white/20 text-white hover:btn-violet hover:border-transparent"
        }`}
      >
        Get Started <ArrowUpRight size={14} />
      </a>
    </div>
  );
};

export const Pricing = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "center",
    skipSnaps: false,
    breakpoints: {
      "(min-width: 768px)": { align: "start" },
    },
  });
  const [selectedIdx, setSelectedIdx] = useState(0);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIdx(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    // jump to most popular initially
    const popularIdx = SERVICES.findIndex((s) => s.slug === MOST_POPULAR_SLUG);
    if (popularIdx > -1) setTimeout(() => emblaApi.scrollTo(popularIdx, true), 300);
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);

  return (
    <section
      data-testid="pricing-section"
      id="pricing"
      className="relative py-24 md:py-32 border-t border-white/10 neon-glow-bg overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <ScrollReveal className="mb-12 md:mb-16 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <div className="text-xs uppercase tracking-[0.4em] text-violet-300 mb-4">
              / Pricing
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-black uppercase tracking-tight leading-[1]">
              Transparent.<br />
              <span className="text-violet-glow">No surprises.</span>
            </h2>
          </div>
          <p className="text-white/60 max-w-md text-sm md:text-base leading-relaxed">
            Starting prices in INR. Swipe through the deck — every project gets a
            custom quote on a discovery call.
          </p>
        </ScrollReveal>

        {/* Carousel */}
        <div className="relative">
          <div className="overflow-hidden pt-8 pb-4 -mx-2 px-2" ref={emblaRef} data-testid="pricing-carousel">
            <div className="flex -ml-4 md:-ml-6">
              {SERVICES.map((s) => (
                <div
                  key={s.slug}
                  className="pl-4 md:pl-6 shrink-0 grow-0 basis-[88%] sm:basis-[60%] md:basis-1/2 lg:basis-1/3 py-3"
                >
                  <Card s={s} />
                </div>
              ))}
            </div>
          </div>

          {/* Nav buttons */}
          <button
            onClick={scrollPrev}
            data-testid="pricing-prev"
            aria-label="Previous"
            className="hidden md:flex absolute -left-3 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black border border-violet-400/40 text-white items-center justify-center hover:bg-violet-500 hover:border-violet-500 transition-colors z-10"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={scrollNext}
            data-testid="pricing-next"
            aria-label="Next"
            className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black border border-violet-400/40 text-white items-center justify-center hover:bg-violet-500 hover:border-violet-500 transition-colors z-10"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        {/* Dots */}
        <div className="flex items-center justify-center gap-2 mt-10">
          {SERVICES.map((s, i) => (
            <button
              key={s.slug}
              data-testid={`pricing-dot-${i}`}
              onClick={() => emblaApi && emblaApi.scrollTo(i)}
              aria-label={`Go to ${s.title}`}
              className={`h-1.5 rounded-full transition-all ${
                selectedIdx === i
                  ? "w-10 bg-violet-400 shadow-[0_0_12px_rgba(139,92,246,0.8)]"
                  : "w-4 bg-white/20"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Pricing;
